import React, { ReactNode } from 'react';
import { Box, Button, PaletteColor, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { Switch } from '../../atoms';

export type AccordionHeaderProps = {
    subtitle?: string;
    title: ReactNode;
    expandLabel?: ReactNode;
    expandColor?: PaletteColor | string;
    expanded: boolean;
    onToggle: () => void;
    sx?: SxProps;
    titlesSx?: SxProps;
    checkBox?: boolean;
    checked?: boolean;
    checkBoxDisabled?: boolean;
    onChange?: (checked: boolean) => void;
};

export const AccordionHeader: React.FunctionComponent<AccordionHeaderProps> = function (props) {
    const { palette } = useTheme();
    const {
        title,
        subtitle,
        expandLabel,
        expandColor = palette.primary,
        expanded,
        onToggle,
        sx,
        titlesSx,
        checkBox = false,
        checked = false,
        checkBoxDisabled = false,
        onChange,
    } = props;

    const headerSx: SxProps = {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 25px 20px 25px',
        borderBottomColor: palette.grey[200],
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        ...sx,
    };

    const expandSx: SxProps = {
        color: typeof expandColor === 'string' ? expandColor : expandColor.main,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0 12px',
    };
    const infoSx: SxProps = {
        display: 'flex',
        alignItems: 'center',
        gap: '0 12px',
    };

    const iconSx: SxProps = {
        lineHeight: 0,
        fontSize: '16px',
        fontWeight: '400',
        display: 'flex',
        alignItems: 'center',
    };

    const subtitlesSx: SxProps = {
        width: '100%',
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '15.6px',
        marginTop: '5px',
        color: 'text.gris50',
    };
    return (
        <Button
            role="button"
            data-testid="accordion-header"
            sx={headerSx}
            onClick={onToggle}
            color="secondary"
        >
            <Box sx={infoSx}>
                {checkBox && onChange ? (
                    <Tooltip title={checkBoxDisabled ? 'Element non sélectionnable' : null}>
                        <Switch
                            size="small"
                            aria-label="switch"
                            disabled={checkBoxDisabled}
                            checked={checked}
                            onClick={(e) => {
                                e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                            }}
                            onChange={(value, state) => {
                                onChange(state);
                            }}
                        />
                    </Tooltip>
                ) : null}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        color={palette.secondary.main}
                        sx={titlesSx}
                    >
                        {title}
                    </Typography>
                    {subtitle ? <Typography sx={subtitlesSx}>{subtitle}</Typography> : null}
                </Box>
            </Box>
            <Box sx={expandSx}>
                {expandLabel}
                <FaOptionIcon
                    sx={iconSx}
                    icon={['fas', expanded ? 'chevron-up' : 'chevron-down']}
                    size="xs"
                />
            </Box>
        </Button>
    );
};
